import React, { useState, useEffect } from 'react';
import { Settings, Key, User, Bell, Shield, Save, HelpCircle, DollarSign, Link2, Copy, Plus, Trash2, AlertTriangle, RefreshCw } from 'lucide-react';
import Layout from '../components/Layout';
import Loading from '../components/Loading';
import { userService } from '../services/api';
import { getUserFromToken } from '../utils/auth';
import { getErrorMessage } from '../utils/helpers';
import toast from 'react-hot-toast';
import KisApiGuideModal from '../components/KisApiGuideModal';
import { useNavigate } from 'react-router-dom';

const SettingsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [apiKeysStatus, setApiKeysStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [showKisGuideModal, setShowKisGuideModal] = useState(false);
  
  // AI 투자 설정
  const [aiInvestmentSettings, setAiInvestmentSettings] = useState({
    ai_investment_limit: 0,
    max_position_per_stock: 5.0,
    auto_trading_enabled: true,
    risk_level: 'MEDIUM',
    trading_strategy: { mean_reversion_pct: 50, breakout_pct: 50 },
    trading_slots: { slot_0930: true, slot_1300: true, slot_1430: true, friday_sell: true },
    us_investment_limit: 0,
    us_auto_trading_enabled: false,
  });
  
  const [apiKeys, setApiKeys] = useState({
    kis_app_key: '',
    kis_app_secret: '',
    kis_account_number: '',
  });

  // MCP 토큰 관리
  const [mcpTokens, setMcpTokens] = useState([]);
  const [mcpTokenLabel, setMcpTokenLabel] = useState('');
  const [mcpTokenExpiry, setMcpTokenExpiry] = useState(0);
  const [newlyGeneratedToken, setNewlyGeneratedToken] = useState(null);
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);
  const [isLoadingTokens, setIsLoadingTokens] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const userData = getUserFromToken();
        setUser(userData);
        
        const statusResponse = await userService.getApiKeysStatus();
        setApiKeysStatus(statusResponse.data);
        
        // AI 투자 설정 불러오기
        try {
          const aiSettingsResponse = await userService.getAiInvestmentSettings();
          const loadedSettings = aiSettingsResponse.data;

          // trading_slots가 없거나 null인 경우 기본값 병합
          const defaultSlots = {
            slot_0930: true,
            slot_1300: true,
            slot_1430: true,
            friday_sell: true
          };
          const defaultStrategy = {
            mean_reversion_pct: 50,
            breakout_pct: 50,
          };

          setAiInvestmentSettings({
            ...loadedSettings,
            trading_slots: loadedSettings.trading_slots
              ? { ...defaultSlots, ...loadedSettings.trading_slots }
              : defaultSlots,
            trading_strategy: loadedSettings.trading_strategy
              ? { ...defaultStrategy, ...loadedSettings.trading_strategy }
              : defaultStrategy,
            us_investment_limit: loadedSettings.us_investment_limit ?? 0,
            us_auto_trading_enabled: loadedSettings.us_auto_trading_enabled ?? false,
          });

          console.log('AI 투자 설정 로드 완료:', loadedSettings);
          console.log('trading_slots:', loadedSettings.trading_slots);
        } catch (error) {
          console.log('AI 투자 설정 없음 - 기본값 사용');
        }

        // MCP 토큰 목록 불러오기
        try {
          const mcpResponse = await userService.getMcpTokens();
          setMcpTokens(mcpResponse.data.tokens || []);
        } catch (error) {
          console.log('MCP 토큰 목록 로드 실패:', error);
        }
      } catch (error) {
        console.error('Settings data load error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const handleSaveAiSettings = async () => {
    setIsSaving(true);
    try {
      await userService.saveAiInvestmentSettings(aiInvestmentSettings);
      toast.success('AI 투자 설정이 성공적으로 저장되었습니다.');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

const handleSaveApiKeys = async () => {
  setIsSaving(true);
  try {
    // ✅ 전체 API 키 한번에 저장 방식으로 변경
    const apiData = {api_name: 'kis',};
    
    if (apiKeys.kis_app_key) apiData.kis_app_key = apiKeys.kis_app_key;
    if (apiKeys.kis_app_secret) apiData.kis_app_secret = apiKeys.kis_app_secret;
    if (apiKeys.kis_account_number) apiData.kis_account_number = apiKeys.kis_account_number;
    // if (apiKeys.chatgpt_api_key) apiData.chatgpt_key = apiKeys.chatgpt_api_key;
    // if (apiKeys.claude_api_key) apiData.claude_key = apiKeys.claude_api_key;
    // if (apiKeys.perplexity_api_key) apiData.perplexity_key = apiKeys.perplexity_api_key;
    
    if (Object.keys(apiData).length > 0) {
      await userService.saveApiKeys(apiData);  // 전체 저장
    }
    
    toast.success('API 키가 성공적으로 저장되었습니다.');
    
    // 상태 새로고침
    const statusResponse = await userService.getApiKeysStatus();
    setApiKeysStatus(statusResponse.data);
    
    // 폼 초기화
    setApiKeys({
      kis_app_key: '',
      kis_app_secret: '',
      chatgpt_api_key: '',
      claude_api_key: '',
      perplexity_api_key: '',
    });
  } catch (error) {
    toast.error(getErrorMessage(error));
  } finally {
    setIsSaving(false);
  }
};

  // MCP 토큰 발급
  const handleGenerateMcpToken = async () => {
    setIsGeneratingToken(true);
    try {
      const response = await userService.generateMcpToken({
        label: mcpTokenLabel || 'Default',
        expires_in_days: mcpTokenExpiry || null,
      });
      setNewlyGeneratedToken(response.data);
      setMcpTokenLabel('');
      setMcpTokenExpiry(0);
      toast.success('MCP 토큰이 발급되었습니다. 이 토큰은 다시 표시되지 않으니 반드시 복사하세요!');
      // 토큰 목록 새로고침
      const mcpResponse = await userService.getMcpTokens();
      setMcpTokens(mcpResponse.data.tokens || []);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsGeneratingToken(false);
    }
  };

  // MCP 토큰 폐기
  const handleRevokeMcpToken = async (tokenId) => {
    if (!window.confirm('이 토큰을 폐기하시겠습니까? 이 토큰을 사용 중인 AI 비서 앱은 더 이상 접속할 수 없습니다.')) return;
    try {
      await userService.revokeMcpToken(tokenId);
      toast.success('토큰이 폐기되었습니다.');
      const mcpResponse = await userService.getMcpTokens();
      setMcpTokens(mcpResponse.data.tokens || []);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // MCP 토큰 목록 새로고침
  const handleRefreshMcpTokens = async () => {
    setIsLoadingTokens(true);
    try {
      const mcpResponse = await userService.getMcpTokens();
      setMcpTokens(mcpResponse.data.tokens || []);
    } catch (error) {
      toast.error('토큰 목록 새로고침 실패');
    } finally {
      setIsLoadingTokens(false);
    }
  };

  // 클립보드 복사
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('클립보드에 복사되었습니다!');
    }).catch(() => {
      // 폴백: 텍스트 선택
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('클립보드에 복사되었습니다!');
    });
  };

  const tabs = [
    { id: 'profile', label: '프로필', icon: User },
    { id: 'ai-investment', label: 'AI 투자 설정', icon: DollarSign },
    { id: 'api-keys', label: 'API 키', icon: Key },
    { id: 'mcp', label: 'MCP 연동', icon: Link2 },
    { id: 'notifications', label: '알림', icon: Bell },
    { id: 'security', label: '보안', icon: Shield },
  ];

  if (isLoading) {
    return (
      <Layout>
        <Loading message="설정 데이터를 불러오는 중..." />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">설정</h1>
          <p className="text-gray-600">계정 및 시스템 설정을 관리하세요</p>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="flex border-b">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-3 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-b-2 border-blue-500 text-blue-600 bg-blue-50'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          <div className="p-6">
            {/* 프로필 탭 */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-gray-900">프로필 정보</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">이메일</label>
                    <input
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">사용자 ID</label>
                    <input
                      type="text"
                      value={user?.id || ''}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                    />
                  </div>
                </div>
                  <h3 className="text-lg font-semibold text-red-600 mb-4 flex items-center">
                    <AlertTriangle className="w-5 h-5 mr-2" />
                    위험 구역
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h4 className="font-medium text-red-800 mb-2">계정 탈퇴</h4>
                      <p className="text-sm text-red-700 mb-4">
                        계정을 삭제하면 모든 데이터가 영구적으로 삭제됩니다. 
                        이 작업은 되돌릴 수 없습니다.
                      </p>
                      <button
                        onClick={() => navigate('/delete-account')}
                        className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        계정 탈퇴하기
                      </button>
                    </div>
                  </div>
              </div>
              
            )}

            {/* AI 투자 설정 탭 */}
            {activeTab === 'ai-investment' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-gray-900">AI 투자 설정</h2>
                  <button
                    onClick={handleSaveAiSettings}
                    disabled={isSaving}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {isSaving ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    <span>{isSaving ? '저장 중...' : 'AI 설정 저장'}</span>
                  </button>
                </div>

                {/* AI 투자 한도 설정 */}
                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 border border-green-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <DollarSign className="w-5 h-5 mr-2 text-green-600" />
                    AI 투자 전용 금액
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        AI가 관리할 투자 금액 (원)
                      </label>
                      <input
                        type="number"
                        value={aiInvestmentSettings.ai_investment_limit}
                        onChange={(e) => setAiInvestmentSettings(prev => ({
                          ...prev, 
                          ai_investment_limit: parseInt(e.target.value) || 0
                        }))}
                        placeholder="예: 5000000 (500만원)"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-lg font-medium"
                      />
                      <p className="text-sm text-gray-600 mt-2">
                        💡 이 금액 범위 내에서만 AI가 자동으로 주식을 매수합니다. 현재 잔고보다 적으면 설정 금액만큼만 투자됩니다.
                      </p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4 border">
                      <p className="text-sm font-medium text-gray-700">현재 설정 요약</p>
                      <div className="mt-2 space-y-1">
                        <p className="text-sm text-gray-600">
                          📊 AI 투자 한도: <span className="font-bold text-green-600">
                            {aiInvestmentSettings.ai_investment_limit.toLocaleString()}원
                          </span>
                        </p>
                        <p className="text-sm text-gray-600">
                          📈 종목당 최대 비중: <span className="font-bold text-blue-600">
                            {aiInvestmentSettings.max_position_per_stock}%
                          </span>
                        </p>
                        <p className="text-sm text-gray-600">
                          🤖 자동매매: <span className={`font-bold ${
                            aiInvestmentSettings.auto_trading_enabled ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {aiInvestmentSettings.auto_trading_enabled ? '활성화' : '비활성화'}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 추가 설정 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      종목당 최대 투자 비중 (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="1"
                      max="20"
                      value={aiInvestmentSettings.max_position_per_stock}
                      onChange={(e) => setAiInvestmentSettings(prev => ({
                        ...prev, 
                        max_position_per_stock: parseFloat(e.target.value) || 5.0
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">한 종목에 투자할 수 있는 최대 비율 (1-20%)</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      투자 위험도
                    </label>
                    <select
                      value={aiInvestmentSettings.risk_level}
                      onChange={(e) => setAiInvestmentSettings(prev => ({
                        ...prev, 
                        risk_level: e.target.value
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="LOW">안정형 (LOW)</option>
                      <option value="MEDIUM">균형형 (MEDIUM)</option>
                      <option value="HIGH">공격형 (HIGH)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">AI 추천 종목 선택 기준</p>
                  </div>
                </div>

                {/* 자동매매 활성화 */}
                <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">AI 자동매매</p>
                      <p className="text-sm text-gray-600">AI가 자동으로 주식을 매수/매도합니다</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={aiInvestmentSettings.auto_trading_enabled}
                        onChange={(e) => setAiInvestmentSettings(prev => ({
                          ...prev,
                          auto_trading_enabled: e.target.checked
                        }))}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                    </label>
                  </div>
                </div>

                {/* 매매 시간대 선택 */}
                <div className="bg-purple-50 rounded-lg p-6 border border-purple-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Bell className="w-5 h-5 mr-2 text-purple-600" />
                    매매 시간대 선택
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    참여할 매매 시간대를 선택하세요. 선택하지 않은 시간대에는 자동매매가 실행되지 않습니다.
                  </p>

                  <div className="space-y-3">
                    {/* 오전 09:30 세션 */}
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                      <div>
                        <p className="font-medium text-gray-900">🌅 오전 세션 (09:30)</p>
                        <p className="text-xs text-gray-500">09:30 분석 → 09:45 계획 → 09:50 매매</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={aiInvestmentSettings.trading_slots?.slot_0930 ?? true}
                          onChange={(e) => setAiInvestmentSettings(prev => ({
                            ...prev,
                            trading_slots: {
                              ...prev.trading_slots,
                              slot_0930: e.target.checked
                            }
                          }))}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                      </label>
                    </div>

                    {/* 오후 13:00 세션 */}
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                      <div>
                        <p className="font-medium text-gray-900">☀️ 오후 세션 (13:00)</p>
                        <p className="text-xs text-gray-500">13:00 분석 → 13:15 계획 → 13:20 매매</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={aiInvestmentSettings.trading_slots?.slot_1300 ?? true}
                          onChange={(e) => setAiInvestmentSettings(prev => ({
                            ...prev,
                            trading_slots: {
                              ...prev.trading_slots,
                              slot_1300: e.target.checked
                            }
                          }))}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                      </label>
                    </div>

                    {/* 오후 14:30 세션 */}
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                      <div>
                        <p className="font-medium text-gray-900">🌆 마감 세션 (14:30)</p>
                        <p className="text-xs text-gray-500">14:30 분석 → 14:45 계획 → 14:50 매매</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={aiInvestmentSettings.trading_slots?.slot_1430 ?? true}
                          onChange={(e) => setAiInvestmentSettings(prev => ({
                            ...prev,
                            trading_slots: {
                              ...prev.trading_slots,
                              slot_1430: e.target.checked
                            }
                          }))}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                      </label>
                    </div>

                    {/* 금요일 전량 매도 */}
                    <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                      <div>
                        <p className="font-medium text-gray-900">🔴 금요일 전량 매도 (15:20)</p>
                        <p className="text-xs text-gray-500">블랙 먼데이 회피를 위한 금요일 장마감 전 전량 매도</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={aiInvestmentSettings.trading_slots?.friday_sell ?? true}
                          onChange={(e) => setAiInvestmentSettings(prev => ({
                            ...prev,
                            trading_slots: {
                              ...prev.trading_slots,
                              friday_sell: e.target.checked
                            }
                          }))}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                      </label>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-white rounded-lg border">
                    <p className="text-sm font-medium text-gray-700">현재 활성 시간대</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {aiInvestmentSettings.trading_slots?.slot_0930 && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">09:30</span>
                      )}
                      {aiInvestmentSettings.trading_slots?.slot_1300 && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">13:00</span>
                      )}
                      {aiInvestmentSettings.trading_slots?.slot_1430 && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">14:30</span>
                      )}
                      {aiInvestmentSettings.trading_slots?.friday_sell && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">금요일 매도</span>
                      )}
                      {!aiInvestmentSettings.trading_slots?.slot_0930 &&
                       !aiInvestmentSettings.trading_slots?.slot_1300 &&
                       !aiInvestmentSettings.trading_slots?.slot_1430 &&
                       !aiInvestmentSettings.trading_slots?.friday_sell && (
                        <span className="text-gray-500 text-xs">선택된 시간대가 없습니다</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* 매매 전략 비중 설정 */}
                <div className="bg-indigo-50 rounded-lg p-6 border border-indigo-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                    <Settings className="w-5 h-5 mr-2 text-indigo-600" />
                    매매 전략 비중
                  </h3>
                  <p className="text-sm text-gray-600 mb-5">
                    두 가지 전략의 비중을 조절하세요. 합계는 항상 100%입니다.
                  </p>

                  {/* 전략 설명 카드 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <p className="font-semibold text-blue-800 text-sm mb-1">🔵 하단 매수 (Mean Reversion)</p>
                      <p className="text-xs text-blue-700">박스권 하단(25% 이내)에서 매수해 중간~상단에서 매도. 횡보장에서 유리. 안정적이지만 수익폭이 제한적.</p>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                      <p className="font-semibold text-orange-800 text-sm mb-1">🔶 돌파 매수 (Breakout)</p>
                      <p className="text-xs text-orange-700">박스권 상단을 돌파할 때 매수해 추가 상승을 노림. 추세장에서 유리. 수익폭이 크지만 가짜 돌파 위험 있음.</p>
                    </div>
                  </div>

                  {/* 슬라이더 */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center text-sm font-medium">
                      <span className="text-blue-700">🔵 하단 매수 {aiInvestmentSettings.trading_strategy?.mean_reversion_pct ?? 50}%</span>
                      <span className="text-orange-700">돌파 매수 {aiInvestmentSettings.trading_strategy?.breakout_pct ?? 50}% 🔶</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="10"
                      value={aiInvestmentSettings.trading_strategy?.mean_reversion_pct ?? 50}
                      onChange={(e) => {
                        const mr = parseInt(e.target.value);
                        setAiInvestmentSettings(prev => ({
                          ...prev,
                          trading_strategy: {
                            mean_reversion_pct: mr,
                            breakout_pct: 100 - mr,
                          }
                        }));
                      }}
                      className="w-full h-3 rounded-lg appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${aiInvestmentSettings.trading_strategy?.mean_reversion_pct ?? 50}%, #f97316 ${aiInvestmentSettings.trading_strategy?.mean_reversion_pct ?? 50}%, #f97316 100%)`
                      }}
                    />
                    {/* 눈금 */}
                    <div className="flex justify-between text-xs text-gray-400 px-0.5">
                      {[0,10,20,30,40,50,60,70,80,90,100].map(v => (
                        <span key={v}>{v === 0 || v === 50 || v === 100 ? v : '|'}</span>
                      ))}
                    </div>
                  </div>

                  {/* 프리셋 버튼 */}
                  <div className="mt-4">
                    <p className="text-xs text-gray-500 mb-2">빠른 선택</p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { label: '안정형 (하단 100%)', mr: 100 },
                        { label: '안정 우선 (70:30)', mr: 70 },
                        { label: '균형 (50:50)', mr: 50 },
                        { label: '공격 우선 (30:70)', mr: 30 },
                        { label: '공격형 (돌파 100%)', mr: 0 },
                      ].map(preset => {
                        const current = aiInvestmentSettings.trading_strategy?.mean_reversion_pct ?? 50;
                        const isActive = current === preset.mr;
                        return (
                          <button
                            key={preset.label}
                            onClick={() => setAiInvestmentSettings(prev => ({
                              ...prev,
                              trading_strategy: {
                                mean_reversion_pct: preset.mr,
                                breakout_pct: 100 - preset.mr,
                              }
                            }))}
                            className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                              isActive
                                ? 'bg-indigo-600 text-white border-indigo-600'
                                : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400 hover:text-indigo-600'
                            }`}
                          >
                            {preset.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* 미국 주식 투자 설정 */}
                <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                    <span className="mr-2 text-xl">🇺🇸</span>
                    미국 주식 자동매매 설정
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">한국투자증권 계좌로 미국 주식을 자동 매매합니다. 자는 동안 미국 장이 운영됩니다.</p>

                  {/* 사전 준비 안내 */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-5">
                    <p className="font-semibold text-amber-800 text-sm mb-1">⚠️ 사전 준비 필수</p>
                    <ul className="text-sm text-amber-700 space-y-1">
                      <li>• 한국투자증권 계좌에 <strong>USD(달러)를 미리 입금</strong>해야 합니다</li>
                      <li>• 설정한 금액만큼 외화 잔고가 없으면 주문이 실패합니다</li>
                      <li>• KIS 앱 → 해외주식 → 외화입금에서 달러 환전/입금 가능합니다</li>
                    </ul>
                  </div>

                  <div className="space-y-4">
                    {/* USD 투자 한도 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        미국 주식 AI 투자 한도 (USD $)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                        <input
                          type="number"
                          value={aiInvestmentSettings.us_investment_limit}
                          onChange={(e) => setAiInvestmentSettings(prev => ({
                            ...prev,
                            us_investment_limit: parseFloat(e.target.value) || 0
                          }))}
                          placeholder="예: 5000"
                          className="w-full pl-7 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg font-medium"
                        />
                      </div>
                      <div className="mt-2 flex items-center gap-3 text-sm">
                        <span className="text-gray-500">≈</span>
                        <span className="font-medium text-gray-700">
                          약 {(Number(aiInvestmentSettings.us_investment_limit || 0) * 1350).toLocaleString()}원
                        </span>
                        <span className="text-xs text-gray-400">(환율 1,350원 기준 참고값)</span>
                      </div>
                    </div>

                    {/* 미국 주식 자동매매 토글 */}
                    <div className="flex items-center justify-between p-4 bg-white rounded-lg border">
                      <div>
                        <p className="font-medium text-gray-900">미국 주식 자동매매 활성화</p>
                        <p className="text-sm text-gray-500">미국 장 시작 시 자동 매수/매도 (한국 시간 23:30~06:00 KST)</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={aiInvestmentSettings.us_auto_trading_enabled}
                          onChange={(e) => setAiInvestmentSettings(prev => ({
                            ...prev,
                            us_auto_trading_enabled: e.target.checked
                          }))}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    {/* 활성화 시 스케줄 안내 */}
                    {aiInvestmentSettings.us_auto_trading_enabled && (
                      <div className="bg-white rounded-lg border p-4">
                        <p className="font-medium text-gray-800 text-sm mb-3">📅 자동 실행 스케줄 (KST 기준)</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-600">
                          <div className="flex items-center gap-2">
                            <span className="w-16 text-right font-mono font-medium text-gray-800">23:00</span>
                            <span className="text-gray-400">→</span>
                            <span>Box 권 분석</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-16 text-right font-mono font-medium text-gray-800">23:15</span>
                            <span className="text-gray-400">→</span>
                            <span>매매 계획 생성</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-16 text-right font-mono font-medium text-blue-700">23:30</span>
                            <span className="text-gray-400">→</span>
                            <span className="font-medium text-blue-700">매매 실행 (장 시작)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-16 text-right font-mono font-medium text-gray-800">02:00</span>
                            <span className="text-gray-400">→</span>
                            <span>리스크 모니터링</span>
                          </div>
                          <div className="flex items-center gap-2 sm:col-span-2">
                            <span className="w-16 text-right font-mono font-medium text-red-700">토 04:50</span>
                            <span className="text-gray-400">→</span>
                            <span className="font-medium text-red-700">금요일 전량 매도 (블랙먼데이 방어)</span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-400 mt-3">* 서머타임(3~11월)에는 1시간 앞당겨집니다 (22:30 장 시작)</p>
                      </div>
                    )}

                    {/* 투자 전략 안내 */}
                    <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500">
                      <p>• 미국 주식도 위의 <strong>매매 전략 비중</strong> 설정(하단매수/돌파매수)이 동일하게 적용됩니다</p>
                      <p className="mt-1">• 분석 대상: AAPL, MSFT, NVDA, GOOGL, META, TSLA, AMD 등 S&P500 대표 30종목</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* API 키 탭 */}
            {activeTab === 'api-keys' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-gray-900">API 키 관리</h2>
                  <button
                    onClick={handleSaveApiKeys}
                    disabled={isSaving}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {isSaving ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    <span>{isSaving ? '저장 중...' : '저장'}</span>
                  </button>
                </div>

                {/* API 키 상태 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">현재 설정 상태</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${apiKeysStatus?.kis_configured ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span className="text-sm text-gray-700">KIS API</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${apiKeysStatus?.account_number_configured ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span className="text-sm text-gray-700">계좌번호</span>
                    </div>
                    {/* <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${apiKeysStatus?.chatgpt_configured ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span className="text-sm text-gray-700">ChatGPT</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${apiKeysStatus?.claude_configured ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span className="text-sm text-gray-700">Claude</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${apiKeysStatus?.perplexity_configured ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span className="text-sm text-gray-700">Perplexity</span>
                    </div> */}
                  </div>
                </div>

                {/* KIS API */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-md font-medium text-gray-900">한국투자증권 KIS API</h3>
                    <button
                      onClick={() => setShowKisGuideModal(true)}
                      className="flex items-center space-x-2 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                    >
                      <HelpCircle className="h-4 w-4" />
                      <span>발급 방법</span>
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">App Key</label>
                      <input
                        type="text"
                        value={apiKeys.kis_app_key}
                        onChange={(e) => setApiKeys(prev => ({...prev, kis_app_key: e.target.value}))}
                        placeholder="KIS App Key 입력"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">App Secret</label>
                      <input
                        type="password"
                        value={apiKeys.kis_app_secret}
                        onChange={(e) => setApiKeys(prev => ({...prev, kis_app_secret: e.target.value}))}
                        placeholder="KIS App Secret 입력"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        계좌번호 
                        <span className="text-xs text-gray-500 ml-1">(8자리-2자리)</span>
                      </label>
                      <input
                        type="text"
                        value={apiKeys.kis_account_number}
                        onChange={(e) => setApiKeys(prev => ({...prev, kis_account_number: e.target.value}))}
                        placeholder="예: 11117009-01"
                        pattern="^\d{8}-\d{2}$"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        💡 KIS 계좌번호를 8자리-2자리 형태로 입력하세요
                      </p>
                    </div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <HelpCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <h4 className="text-sm font-medium text-blue-900">계좌번호 확인 방법</h4>
                        <p className="text-sm text-blue-700 mt-1">
                          KIS 모바일 앱 → 메뉴 → 계좌관리 → 계좌번호 확인<br/>
                          또는 KIS 홈페이지에서 로그인 후 계좌정보에서 확인 가능합니다.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* AI API Keys */}
                {/* <div className="space-y-4">
                  <h3 className="text-md font-medium text-gray-900">AI 서비스 API</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">ChatGPT API Key</label>
                      <input
                        type="password"
                        value={apiKeys.chatgpt_api_key}
                        onChange={(e) => setApiKeys(prev => ({...prev, chatgpt_api_key: e.target.value}))}
                        placeholder="ChatGPT API Key 입력"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Claude API Key</label>
                      <input
                        type="password"
                        value={apiKeys.claude_api_key}
                        onChange={(e) => setApiKeys(prev => ({...prev, claude_api_key: e.target.value}))}
                        placeholder="Claude API Key 입력"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Perplexity API Key</label>
                      <input
                        type="password"
                        value={apiKeys.perplexity_api_key}
                        onChange={(e) => setApiKeys(prev => ({...prev, perplexity_api_key: e.target.value}))}
                        placeholder="Perplexity API Key 입력"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div> */}
              </div>
            )}

            {/* MCP 연동 탭 */}
            {activeTab === 'mcp' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-gray-900">MCP 연동 (AI 비서 연결)</h2>
                  <button
                    onClick={handleRefreshMcpTokens}
                    disabled={isLoadingTokens}
                    className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <RefreshCw className={`w-4 h-4 ${isLoadingTokens ? 'animate-spin' : ''}`} />
                    <span>새로고침</span>
                  </button>
                </div>

                {/* 안내 */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-blue-900 mb-2">MCP 토큰이란?</h3>
                  <p className="text-sm text-blue-800">
                    MCP(Model Context Protocol) 토큰을 발급하면 외부 AI 비서 앱(Claude Desktop 등)에서
                    이 AI Trade 시스템에 안전하게 접속할 수 있습니다.
                    토큰은 발급 시 1회만 표시되며, 언제든 폐기할 수 있습니다.
                  </p>
                </div>

                {/* 토큰 발급 폼 */}
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 border border-purple-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Plus className="w-5 h-5 mr-2 text-purple-600" />
                    새 토큰 발급
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">토큰 이름</label>
                      <input
                        type="text"
                        value={mcpTokenLabel}
                        onChange={(e) => setMcpTokenLabel(e.target.value)}
                        placeholder="예: Claude Desktop"
                        maxLength={100}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">만료 기간</label>
                      <select
                        value={mcpTokenExpiry}
                        onChange={(e) => setMcpTokenExpiry(parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value={0}>무기한</option>
                        <option value={30}>30일</option>
                        <option value={90}>90일</option>
                        <option value={180}>180일</option>
                        <option value={365}>365일</option>
                      </select>
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={handleGenerateMcpToken}
                        disabled={isGeneratingToken}
                        className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                      >
                        {isGeneratingToken ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <Key className="w-4 h-4" />
                        )}
                        <span>{isGeneratingToken ? '발급 중...' : '토큰 발급'}</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* 새로 발급된 토큰 표시 (1회만) */}
                {newlyGeneratedToken && (
                  <div className="bg-green-50 border-2 border-green-400 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-bold text-green-900 flex items-center">
                        <Shield className="w-5 h-5 mr-2" />
                        토큰이 발급되었습니다!
                      </h3>
                      <button
                        onClick={() => setNewlyGeneratedToken(null)}
                        className="text-gray-400 hover:text-gray-600 text-lg"
                      >
                        ✕
                      </button>
                    </div>
                    <p className="text-sm text-red-700 font-semibold mb-3">
                      이 토큰은 지금만 표시됩니다. 반드시 복사하여 안전한 곳에 보관하세요!
                    </p>
                    <div className="bg-white border border-green-300 rounded-lg p-3 flex items-center justify-between">
                      <code className="text-xs text-gray-800 break-all flex-1 mr-3 select-all">
                        {newlyGeneratedToken.token}
                      </code>
                      <button
                        onClick={() => copyToClipboard(newlyGeneratedToken.token)}
                        className="flex-shrink-0 flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm transition-colors"
                      >
                        <Copy className="w-4 h-4" />
                        <span>복사</span>
                      </button>
                    </div>
                    <div className="mt-3 text-sm text-gray-600">
                      <p>토큰 이름: <span className="font-medium">{newlyGeneratedToken.label}</span></p>
                      <p>토큰 ID: <span className="font-mono text-xs">{newlyGeneratedToken.token_id}</span></p>
                    </div>
                  </div>
                )}

                {/* 활성 토큰 목록 */}
                <div>
                  <h3 className="text-md font-semibold text-gray-900 mb-3">
                    활성 토큰 ({mcpTokens.length}개)
                  </h3>
                  {mcpTokens.length === 0 ? (
                    <div className="bg-gray-50 rounded-lg p-6 text-center text-gray-500">
                      <Key className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p>발급된 MCP 토큰이 없습니다.</p>
                      <p className="text-sm mt-1">위에서 새 토큰을 발급하세요.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 text-gray-600">
                            <th className="text-left px-4 py-2 rounded-tl-lg">이름</th>
                            <th className="text-left px-4 py-2">토큰 ID</th>
                            <th className="text-left px-4 py-2">발급일</th>
                            <th className="text-left px-4 py-2">마지막 사용</th>
                            <th className="text-left px-4 py-2">만료일</th>
                            <th className="text-center px-4 py-2 rounded-tr-lg">관리</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mcpTokens.map((token) => (
                            <tr key={token.token_id} className="border-t hover:bg-gray-50">
                              <td className="px-4 py-3 font-medium text-gray-900">{token.label}</td>
                              <td className="px-4 py-3 font-mono text-xs text-gray-500">
                                {token.token_id.substring(0, 8)}...
                              </td>
                              <td className="px-4 py-3 text-gray-600">
                                {token.created_at ? new Date(token.created_at).toLocaleDateString('ko-KR') : '-'}
                              </td>
                              <td className="px-4 py-3 text-gray-600">
                                {token.last_used_at ? new Date(token.last_used_at).toLocaleDateString('ko-KR') : '미사용'}
                              </td>
                              <td className="px-4 py-3 text-gray-600">
                                {token.expires_at ? new Date(token.expires_at).toLocaleDateString('ko-KR') : '무기한'}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <button
                                  onClick={() => handleRevokeMcpToken(token.token_id)}
                                  className="inline-flex items-center space-x-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded transition-colors"
                                >
                                  <Trash2 className="w-3 h-3" />
                                  <span>폐기</span>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* 사용 방법 안내 */}
                <div className="bg-gray-50 rounded-lg p-6 border">
                  <h3 className="text-md font-semibold text-gray-900 mb-3">사용 방법</h3>
                  <div className="space-y-3 text-sm text-gray-700">
                    <div className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                      <p>위에서 <strong>"토큰 발급"</strong> 버튼을 클릭하여 MCP 토큰을 생성합니다.</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                      <p>발급된 토큰을 복사합니다. (토큰은 1회만 표시되니 꼭 복사하세요!)</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                      <div>
                        <p>AI 비서 앱의 MCP 서버 설정에 아래와 같이 입력합니다:</p>
                        <div className="mt-2 bg-gray-900 text-green-400 rounded-lg p-3 font-mono text-xs overflow-x-auto">
                          <pre>{`{
  "mcpServers": {
    "ai-trading": {
      "url": "https://aitrade.tachyon.or.kr/mcp-api/sse",
      "env": {
        "MCP_TOKEN": "여기에_복사한_토큰_붙여넣기"
      }
    }
  }
}`}</pre>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-bold">4</span>
                      <p>AI 비서 앱을 재시작하면 자동으로 연결됩니다.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 알림 탭 */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-gray-900">알림 설정</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <p className="font-medium text-gray-900">거래 알림</p>
                      <p className="text-sm text-gray-500">매수/매도 거래 시 알림을 받습니다</p>
                    </div>
                    <input type="checkbox" className="h-4 w-4 text-blue-600 rounded" defaultChecked />
                  </div>
                  <div className="flex items-center justify-between py-3 border-t">
                    <div>
                      <p className="font-medium text-gray-900">AI 분석 알림</p>
                      <p className="text-sm text-gray-500">새로운 AI 분석 결과 시 알림을 받습니다</p>
                    </div>
                    <input type="checkbox" className="h-4 w-4 text-blue-600 rounded" defaultChecked />
                  </div>
                  <div className="flex items-center justify-between py-3 border-t">
                    <div>
                      <p className="font-medium text-gray-900">시스템 알림</p>
                      <p className="text-sm text-gray-500">시스템 상태 변경 시 알림을 받습니다</p>
                    </div>
                    <input type="checkbox" className="h-4 w-4 text-blue-600 rounded" />
                  </div>
                </div>
              </div>
            )}

            {/* 보안 탭 */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-gray-900">보안 설정</h2>
                <div className="space-y-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-yellow-800">보안 설정 기능이 개발 중입니다.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* KIS API 가이드 모달 */}
        <KisApiGuideModal 
          isOpen={showKisGuideModal} 
          onClose={() => setShowKisGuideModal(false)} 
        />
      </div>
    </Layout>
  );
};

export default SettingsPage;